import React, { useCallback, useMemo } from 'react';

import { useHistory } from 'react-router-dom';
import { Box, Divider, Typography } from '@material-ui/core';

import { HoldingPage } from '@vyce/core/src/components/HoldingPage';
import { NO_MODULE_ACCESS_TEXT } from '@vyce/core/src/constants';
import config from '@vyce/core/src/assets/config';

import { useSubscriptionData } from '../../../modules/subscriptionInfoModule/hooks/useSubscriptionData';
import { ConfirmMessagingSubscriptionDialog } from './ConfirmMessagingSubscriptionDialog';

interface Props {
  hadSubscription?: boolean;
  hasPermissions?: boolean;
  hasSubscription?: boolean;
}

export const GetStarted: React.FC<Props> = ({ hadSubscription, hasPermissions, hasSubscription }) => {
  const history = useHistory();
  const {
    openMessagingSubscriptionDialog,
    closeMessagingSubscriptionDialog,
    addMessagingSubscription,
    isMessagingSubscriptionDialogOpen,
    loadingMessagingSubscription,
    messagingSubscription,
  } = useSubscriptionData();

  const textInsteadButton = hasPermissions ? '' : NO_MODULE_ACCESS_TEXT;
  const buttonText = useMemo(() => {
    if (hadSubscription) return 'Reactivate your Messages Module now...';
    if (!hasSubscription || !messagingSubscription) return 'Activate your Messages Module now...';
    return "Let's start";
  }, [hadSubscription, hasSubscription, messagingSubscription]);

  const goToSubscription = () => {
    history.push('/subscriptions?dialog=true');
  };

  const subscribeToMessaging = () => {
    addMessagingSubscription();
  };

  const handleClick = useCallback(() => {
    if (hadSubscription || !hasSubscription) {
      return goToSubscription();
    }
    if (hasSubscription && !messagingSubscription) {
      openMessagingSubscriptionDialog();
    }
  }, [hadSubscription, messagingSubscription, hasSubscription, addMessagingSubscription]);

  return (
    <>
      <ConfirmMessagingSubscriptionDialog
        open={isMessagingSubscriptionDialogOpen}
        setOpen={closeMessagingSubscriptionDialog}
        confirmFunction={subscribeToMessaging}
        loading={loadingMessagingSubscription}
      />

      <HoldingPage
        title="Messages"
        buttonText={buttonText}
        handleButtonClick={handleClick}
        textInsteadButton={textInsteadButton}
        image={config.welcome.messages}>
        <Box marginBottom={4}>
          <Box marginTop={1} display="flex">
            <Typography>All your work conversations. One simple, secure space.</Typography>
          </Box>

          <Box marginTop={3}>
            <Typography style={{ fontWeight: 600 }}>Separate work from personal life</Typography>
          </Box>

          <Box marginTop={1}>
            <Typography>
              No more mixing work chats with personal group messages. Keep everything professional, focused,
              and easy to find.
            </Typography>
          </Box>

          <Divider style={{ margin: '8px 0' }} />

          <Box marginTop={3}>
            <Typography style={{ fontWeight: 600 }}>Control access with confidence</Typography>
          </Box>

          <Box marginTop={1}>
            <Typography>
              Easily manage who sees what. From private threads to project-specific documents, you're in
              control.
            </Typography>
          </Box>

          <Divider style={{ margin: '8px 0' }} />

          <Box marginTop={3}>
            <Typography style={{ fontWeight: 600 }}>Organized conversation threads</Typography>
          </Box>

          <Box marginTop={1}>
            <Typography>
              Say goodbye to scattered messages across different apps. Keep context-rich chats, files, and
              follow-ups all together — and always accessible.
            </Typography>
          </Box>
        </Box>
      </HoldingPage>
    </>
  );
};
